import { ErrorHandler, Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from './toast.service';

/** Minimum time between repeat toasts for the same error message, in ms. */
const REPEAT_TOAST_SUPPRESS_MS = 4000;

/**
 * Catches uncaught application errors — component/template exceptions and
 * unhandled promise rejections routed here via `provideBrowserGlobalErrorListeners()`
 * — that would otherwise only be visible in the browser console.
 *
 * This is the single place uncaught errors flow through; wire a real telemetry
 * vendor (Sentry, LogRocket, etc.) into `report()` when one is adopted.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  private lastNotifiedAt = 0;

  handleError(error: unknown): void {
    // HTTP errors already get a user-facing toast from errorInterceptor and
    // are re-thrown through the Observable chain, not the ErrorHandler — but
    // guard here too in case one ever surfaces via an unhandled rejection.
    if (error instanceof HttpErrorResponse) {
      this.report(error);
      return;
    }

    this.report(error);
    this.notifyUser();
  }

  private report(error: unknown): void {
    console.error('[GlobalErrorHandler]', error);
  }

  private notifyUser(): void {
    const now = Date.now();
    if (now - this.lastNotifiedAt < REPEAT_TOAST_SUPPRESS_MS) return;
    this.lastNotifiedAt = now;

    try {
      this.toast.error(
        this.translate.instant('COMMON.UNEXPECTED_ERROR_TITLE'),
        this.translate.instant('COMMON.UNEXPECTED_ERROR_MESSAGE'),
      );
    } catch {
      // Notifying the user must never itself throw from inside an error handler.
    }
  }
}
